import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/PhoneInput';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';

/** Cadastro de novo contato — telefone + nome são o mínimo. */
export function ContactFormDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const t = useT();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setPhone(''); setName(''); setEmail(''); };

  const submit = async () => {
    if (!phone.trim()) { toast.error(t('contacts.phoneRequired')); return; }
    setSaving(true);
    const r = await apiFetch('POST', '/contacts', {
      phone: phone.trim(),
      name: name.trim() || undefined,
      email: email.trim().toLowerCase() || undefined,
    });
    setSaving(false);
    if (!r.ok) {
      toast.error(r.status === 409 ? t('contacts.alreadyExists') : ((r.data as { message?: string } | null)?.message ?? t('contacts.createFailed')));
      return;
    }
    toast.success(t('contacts.created'));
    reset();
    onCreated();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="size-5" /> {t('contacts.newContact')}</DialogTitle>
          <DialogDescription>{t('contacts.newContactHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>{t('contacts.phone')}</Label>
            <PhoneInput value={phone} onChange={setPhone} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('contacts.name')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('contacts.namePlaceholder')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('contacts.email')}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@exemplo.com" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
            {t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
